import { useState, useEffect, useRef } from "react";
import { shuffleArray } from "../utils/shuffle";
import { GAME_CONFIG, LEVEL_SETTINGS, STORAGE_KEY } from "../constants";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import flipAudio from "../assets/audio/flip.wav";
import matchAudio from "../assets/audio/match.wav";
import gameOverAudio from "../assets/audio/game-over.wav";
import fiveSecondsCountDownAudio from "../assets/audio/5-seconds-countdown.wav";
import levelCompleteAudio from "../assets/audio/level-complete.wav";

/** ------------------ TYPES ------------------ */
export type CardType = {
  id: number;
  value: string;
  isFlipped: boolean;
  isMatched: boolean;
};

export interface GameStats {
  moves: number;
  matches: number; // number of pairs matched
  points: number;
  timeLeft: number;
  level: number;
}

export interface IProgressService {
  saveProgress: (stats: GameStats) => Promise<void>;
  loadProgress: () => Promise<{ stats: GameStats } | null>;
  clearProgress: () => Promise<void>;
}

/** ------------------ EXTRA POOL ------------------ */
const EXTRA_EMOJI_POOL = [
  "🍌",
  "🍇",
  "🍓",
  "🍑",
  "🍍",
  "🥝",
  "🍉",
  "🍒",
  "🥥",
  "🍊",
  "🐶",
  "🐱",
  "🐭",
  "🐼",
  "🦊",
  "🐻",
  "🐯",
  "🦁",
  "🐨",
  "🦄",
  "✈️",
  "🚲",
  "🛵",
  "🚂",
  "🚁",
  "🛰️",
  "🛸",
  "🏎️",
  "🚤",
  "🚜",
  "🎹",
  "🎺",
  "🎷",
  "🎻",
  "🎸",
  "🪗",
  "🎯",
  "🧩",
  "🪀",
  "🎳",
  "🌟",
  "🔥",
  "💧",
  "🍀",
  "⭐",
  "⚡",
  "☃️",
  "🌈",
  "🔔",
  "🔑",
];

/** ------------------ LOCAL STORAGE SERVICE ------------------ */
const LocalProgressService: IProgressService = {
  async saveProgress(stats) {
    if (!GAME_CONFIG.SAVE_PROGRESS) return;
    try {
      const minimalStats = {
        level: stats.level,
        points: stats.points,
      };
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ stats: minimalStats })
      );
    } catch {
      // ignore storage errors
    }
  },
  async loadProgress() {
    if (!GAME_CONFIG.SAVE_PROGRESS) return null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw); // { stats: { level, points } }
    } catch {
      return null;
    }
  },
  async clearProgress() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },
};

/** ------------------ HELPERS: level config & chosen values ------------------ */
const computeGridForLevel = (level: number, startGrid: number) => {
  return LEVEL_SETTINGS[level]?.gridSize;
};

const computeTimeForLevel = (level: number) => {
  return LEVEL_SETTINGS[level]?.timeLimit;
};

const computeFlipBackDelayForLevel = (level: number) => {
  return LEVEL_SETTINGS[level]?.flipBackDelay;
};

const buildChosenValues = (pairCount: number) => {
  const base = Array.isArray(GAME_CONFIG.CARD_VALUES)
    ? [...GAME_CONFIG.CARD_VALUES]
    : [];
  const uniqueBase = Array.from(new Set(base));
  if (uniqueBase.length >= pairCount) return uniqueBase.slice(0, pairCount);

  const pool = [...uniqueBase];
  for (const e of EXTRA_EMOJI_POOL) {
    if (pool.length >= pairCount) break;
    if (!pool.includes(e)) pool.push(e);
  }

  let i = 0;
  while (pool.length < pairCount) {
    const token = `#${i++}`;
    if (!pool.includes(token)) pool.push(token);
  }

  return pool.slice(0, pairCount);
};

/** ------------------ HOOK ------------------ */
/**
 * usePairsGame
 * @param options.startLevel optional starting level (defaults to 1)
 * Additional options:
 * - onWin(stats) called when level completes
 * - onLose(stats) called when time expires / game over
 * - onMatch({ firstId, secondId, stats }) called when match found
 * - onMismatch({ firstId, secondId, stats }) called when mismatch happens
 * - progressService: custom persistence service (defaults to LocalProgressService)
 */
export function usePairsGame(options: {
  startLevel?: number;
  onWin?: (stats: GameStats) => void;
  onLose?: (stats: GameStats) => void;
  onMatch?: (info: {
    firstId: number;
    secondId: number;
    stats: GameStats;
  }) => void;
  onMismatch?: (info: {
    firstId: number;
    secondId: number;
    stats: GameStats;
  }) => void;
  progressService?: IProgressService;
}) {
  const startGridSize = Number(GAME_CONFIG.GRID_SIZE) ?? 4;
  const startLevel = Math.max(1, Math.floor(options?.startLevel ?? 1));

  // store callbacks & service in refs so they won't be stale
  const onWinRef = useRef<typeof options.onWin>(null);
  const onLoseRef = useRef<typeof options.onLose>(null);
  const onMatchRef = useRef<typeof options.onMatch>(null);
  const onMismatchRef = useRef<typeof options.onMismatch>(null);
  const progressServiceRef = useRef<IProgressService>(
    options?.progressService ?? LocalProgressService
  );

  useEffect(() => {
    onWinRef.current = options?.onWin;
    onLoseRef.current = options?.onLose;
    onMatchRef.current = options?.onMatch;
    onMismatchRef.current = options?.onMismatch;
    progressServiceRef.current =
      options?.progressService ?? LocalProgressService;
  }, [options]);

  // derived/current dynamic settings (stateful because they change each level)
  const [currentLevel, setCurrentLevel] = useState<number>(startLevel);
  const [currentGridSize, setCurrentGridSize] = useState<number>(() =>
    computeGridForLevel(startLevel, startGridSize)
  );
  const [currentTimeLimit, setCurrentTimeLimit] = useState<number>(() =>
    computeTimeForLevel(startLevel)
  );
  const [currentFlipBackDelay, setCurrentFlipBackDelay] = useState<number>(() =>
    computeFlipBackDelayForLevel(startLevel)
  );

  // Pair count derived from currentGridSize
  const totalCards = currentGridSize * currentGridSize;
  const pairCount = totalCards / 2;

  /** generate deck based on currentGridSize / pairCount */
  const generateCards = (gridSize = currentGridSize): CardType[] => {
    const total = gridSize * gridSize;
    const pairs = total / 2;
    const chosen = buildChosenValues(pairs);

    const paired: CardType[] = [];
    for (let i = 0; i < chosen.length; i++) {
      const value = chosen[i];
      paired.push({ id: i * 2, value, isFlipped: false, isMatched: false });
      paired.push({ id: i * 2 + 1, value, isFlipped: false, isMatched: false });
    }

    if (paired.length !== total) {
      const adjusted = paired.map((c, idx) => ({ ...c, id: idx }));
      while (adjusted.length < total) {
        const fillerValue = chosen[0] ?? "X";
        const idx = adjusted.length;
        adjusted.push({
          id: idx,
          value: fillerValue,
          isFlipped: false,
          isMatched: false,
        });
      }
      return shuffleArray(adjusted);
    }

    return shuffleArray(paired);
  };

  /** ------------------ STATE ------------------ */
  const [cards, setCards] = useState<CardType[]>(() => generateCards());
  const [stats, setStats] = useState<GameStats>(() => ({
    moves: 0,
    matches: 0,
    points: 0,
    timeLeft: currentTimeLimit,
    level: currentLevel,
  }));
  const [flipped, setFlipped] = useState<number[]>([]);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isGameOver, setIsGameOver] = useState<boolean>(false);
  const [isLevelComplete, setIsLevelComplete] = useState<boolean>(false);
  const [playingCountdown, setPlayingCountdown] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);

  // timers / intervals
  const timersRef = useRef<number[]>([]);

  // audio helper
  const { playEffect } = useAudioEffects();

  /** ------------------ PERSISTENCE: load saved progress if available ------------------ */
  useEffect(() => {
    (async () => {
      const saved = await progressServiceRef.current.loadProgress();
      if (saved?.stats) {
        const savedLevel = saved.stats.level;
        const savedPoints = saved.stats.points;

        const expectedGrid = computeGridForLevel(savedLevel, startGridSize);
        const expectedTime = computeTimeForLevel(savedLevel);

        setStats({
          moves: 0,
          matches: 0,
          points: savedPoints,
          timeLeft: expectedTime,
          level: savedLevel,
        });
        setCurrentLevel(savedLevel);
        setCurrentGridSize(expectedGrid);
        setCurrentTimeLimit(expectedTime);
        setCurrentFlipBackDelay(computeFlipBackDelayForLevel(savedLevel));
        setCards(generateCards(expectedGrid));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // save minimal progress (level & points) when either changes
    progressServiceRef.current.saveProgress(stats).catch(() => {
      // ignore save errors
    });
  }, [stats.level, stats.points]);

  /** ------------------ TIMER ------------------ */
  const decreaseTime = () => {
    setStats((s) => ({ ...s, timeLeft: s.timeLeft - 1 }));
  };

  const handleTimeExpired = () => {
    setIsRunning(false);
    setIsGameOver(true);
    playEffect(gameOverAudio, { volume: 0.5 });
    // callback
    try {
      onLoseRef.current?.(stats);
    } catch {
      // ignore callback errors
    }
  };

  useEffect(() => {
    if (!isRunning || stats.timeLeft <= 0) return;
    const intervalId = window.setInterval(() => decreaseTime(), 1000);
    timersRef.current.push(intervalId);
    return () => {
      clearInterval(intervalId);
      timersRef.current = timersRef.current.filter((t) => t !== intervalId);
    };
  }, [isRunning, stats.timeLeft]);

  useEffect(() => {
    if (stats.timeLeft <= 5 && !playingCountdown) {
      playEffect(fiveSecondsCountDownAudio, { inBackground: true });
      setPlayingCountdown(true);
    }
    if (stats.timeLeft <= 0 && isRunning) {
      handleTimeExpired();
    }
  }, [stats.timeLeft, isRunning]); // intentionally not including playingCountdown

  /** ------------------ HELPERS: timers clean ------------------ */
  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  /** ------------------ MATCH LOGIC ------------------ */
  const handleCorrectMatch = (firstId: number, secondId: number) => {
    playEffect(matchAudio);

    setCards((prev) =>
      prev.map((c) =>
        c.id === firstId || c.id === secondId ? { ...c, isMatched: true } : c
      )
    );

    setStats((s) => {
      const next = {
        ...s,
        moves: s.moves + 1,
        matches: s.matches + 1,
        points: s.points + GAME_CONFIG.POINTS_MATCH,
      };
      // call onMatch with the new stats
      try {
        onMatchRef.current?.({ firstId, secondId, stats: next });
      } catch {
        // ignore callback errors
      }
      return next;
    });
  };

  // handleWrongMatch only updates stats now — flip-back done centrally in useEffect
  const handleWrongMatch = (firstId: number, secondId: number) => {
    setStats((s) => {
      const next = {
        ...s,
        moves: s.moves + 1,
        points: s.points + GAME_CONFIG.POINTS_MISMATCH,
      };
      // call onMismatch with the new stats
      try {
        onMismatchRef.current?.({ firstId, secondId, stats: next });
      } catch {
        // ignore callback errors
      }
      return next;
    });
  };

  /** ------------------ CARD FLIP ------------------ */
  const flipCard = (id: number) => {
    if (!isRunning || isGameOver || isLevelComplete || isChecking) return;

    playEffect(flipAudio);

    const clicked = cards.find((c) => c.id === id);

    if (!clicked || clicked.isFlipped || clicked.isMatched) return;

    setCards((prev) =>
      prev.map((c) => (c.id === id ? { ...c, isFlipped: true } : c))
    );
    setFlipped((f) => [...f, id]);
  };

  /** ------------------ CHECK MATCH ------------------ */
  useEffect(() => {
    if (flipped.length < 2) return;

    setIsChecking(true);

    const [firstId, secondId] = flipped;
    const first = cards.find((c) => c.id === firstId);
    const second = cards.find((c) => c.id === secondId);
    if (!first || !second) {
      setFlipped([]);
      setIsChecking(false);
      return;
    }

    const isMatch = first.value === second.value;
    if (isMatch) {
      handleCorrectMatch(firstId, secondId);
      setFlipped([]);
      // unlock immediately (match leaves cards face-up as isMatched=true)
      setIsChecking(false);
    } else {
      const flipBackTimer = window.setTimeout(() => {
        setCards((prev) =>
          prev.map((c) =>
            c.id === firstId || c.id === secondId
              ? { ...c, isFlipped: false }
              : c
          )
        );
        handleWrongMatch(firstId, secondId);
        setIsChecking(false);
      }, currentFlipBackDelay);

      timersRef.current.push(flipBackTimer);
      setFlipped([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flipped, cards, currentFlipBackDelay]);

  /** ------------------ LEVEL COMPLETE CHECK ------------------ */
  const handleLevelComplete = () => {
    setIsRunning(false);
    setIsLevelComplete(true);
    playEffect(levelCompleteAudio, { volume: 0.5 });
    // call onWin with current stats
    try {
      onWinRef.current?.(stats);
    } catch {
      // ignore callback errors
    }
  };

  useEffect(() => {
    const currentPairCount = (currentGridSize * currentGridSize) / 2;
    if (stats.matches >= currentPairCount && !isGameOver) {
      handleLevelComplete();
    }
  }, [stats.matches, currentGridSize, isGameOver]);

  /** ------------------ ACTIONS: reset / nextLevel ------------------ */
  const resetGame = async () => {
    clearTimers();
    const gridForLevel = computeGridForLevel(startLevel, startGridSize);
    const timeForLevel = computeTimeForLevel(startLevel);
    const flipDelayForLevel = computeFlipBackDelayForLevel(startLevel);

    setCurrentLevel(startLevel);
    setCurrentGridSize(gridForLevel);
    setCurrentTimeLimit(timeForLevel);
    setCurrentFlipBackDelay(flipDelayForLevel);

    setCards(generateCards(gridForLevel));
    setFlipped([]);

    setPlayingCountdown(false);
    setStats({
      moves: 0,
      matches: 0,
      points: 0,
      timeLeft: timeForLevel,
      level: startLevel,
    });
    setIsRunning(true);
    setIsGameOver(false);
    setIsLevelComplete(false);
    await progressServiceRef.current.clearProgress();
  };

  const nextLevel = () => {
    clearTimers();
    const next = currentLevel + 1;
    const newGrid = computeGridForLevel(next, startGridSize);
    const newTime = computeTimeForLevel(next);
    const newFlipDelay = computeFlipBackDelayForLevel(next);

    setCurrentLevel(next);
    setCurrentGridSize(newGrid);
    setCurrentTimeLimit(newTime);
    setCurrentFlipBackDelay(newFlipDelay);

    setCards(generateCards(newGrid));
    setFlipped([]);

    setPlayingCountdown(false);
    setStats((s) => ({
      moves: 0,
      matches: 0,
      points: s.points,
      timeLeft: newTime,
      level: next,
    }));
    setIsRunning(true);
    setIsGameOver(false);
    setIsLevelComplete(false);
  };

  // cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    // game state
    cards,
    stats,
    flipped,
    isRunning,
    isGameOver,
    isLevelComplete,
    isChecking, // expose to parent so Card can be disabled
    // level / config
    currentGridSize,
    currentTimeLimit,
    currentFlipBackDelay,
    currentLevel,
    // actions
    flipCard,
    resetGame,
    nextLevel,
    startGame: () => setIsRunning(true),
  };
}
